import React from 'react';
import { useTranslation } from 'react-i18next';
import { PeriodComparison as PeriodComparisonType } from '@/types/AnalysisResultType';

interface PeriodComparisonProps {
    comparison?: PeriodComparisonType;
    valueType?: 'time' | 'number' | 'percentage';
    className?: string;
}

/**
 * 前期比表示コンポーネント
 */
const PeriodComparison: React.FC<PeriodComparisonProps> = ({
    comparison,
    valueType = 'percentage',
    className = '',
}) => {
    const { t } = useTranslation('common');
    // 前期比データがない場合、または前期データがない場合は何も表示しない
    if (
        !comparison ||
        comparison.previousValue === undefined ||
        comparison.previousValue === null
    ) {
        return null;
    }

    const { isIncrease, changePercentage, changeAmount } = comparison;

    // 0.0%の場合は矢印と色を表示しない
    const isZeroChange = Math.abs(changePercentage) < 0.1;

    // アイコン（0.0%の場合は表示しない）
    const icon = isZeroChange ? '' : isIncrease ? '↑' : '↓';

    // 小数点以下1桁で表示（.0の場合は整数表示）
    const absPercent = Math.abs(changePercentage);
    const formattedPercentage =
        absPercent % 1 === 0 ? absPercent.toString() : absPercent.toFixed(1);

    // 色のクラス
    const colorClass =
        // 0.0%の場合は黒色
        isZeroChange
            ? 'text-gray-500'
            : // 増加の場合は緑色
              isIncrease
              ? 'text-green-600'
              : // 減少の場合は赤色
                'text-red-600';

    // 実数値の表示形式
    const formatValue = (value: number): string => {
        const absVal = Math.abs(value);
        switch (valueType) {
            // 時間の場合
            case 'time': {
                const hours = Math.floor(absVal / 60);
                const minutes = Math.round(absVal % 60);
                const parts: string[] = [];
                if (hours > 0) {
                    parts.push(t('time.hoursShort', { count: hours }));
                }
                if (minutes > 0) {
                    parts.push(t('time.minutesShort', { count: minutes }));
                }
                if (parts.length === 0) {
                    return t('time.minutesShort', { count: 0 });
                }
                return parts.join('');
            }
            // パーセントの場合
            case 'percentage':
                return `${absVal.toFixed(1)}%`;
            // 数値の場合
            case 'number':
            default:
                return Math.round(absVal).toString();
        }
    };

    return (
        <div className={`flex items-baseline gap-1 ${colorClass} ${className}`}>
            {icon && <span className="font-bold">{icon}</span>}
            <span>{formattedPercentage}%</span>
            <span> {formatValue(changeAmount)}</span>
        </div>
    );
};

export default PeriodComparison;
